import dynamic from 'next/dynamic';
import React, { Suspense } from 'react';
import classes from './CountryCode.module.scss';
import Select, { SingleValue } from 'react-select';
import { graphql, useLazyLoadQuery, usePreloadedQuery, useQueryLoader } from 'react-relay';
import { CountryCodeQuery, CountryCodeQuery$data } from './__generated__/CountryCodeQuery.graphql';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

export type Props = {
  alpha2Code: string;
  alpha3Code: string;
  phoneCode: number;
};

interface CountryCodeOption {
  value: string;
  label: string;
  flagIcon?: string;
}

const query = graphql`
  query CountryCodeQuery {
    panelConfigurations {
      countryDetails {
        phoneNumberPrefixes
        alpha2
        alpha3
      }
    }
  }
`;

export default function CountryCode({ alpha2Code, alpha3Code, phoneCode }: Props) {
  const { t } = useTranslation();

  const defaultCountryOnLoad: CountryCodeOption = {
    value: alpha3Code,
    label: `+${phoneCode}`,
    flagIcon: `/assets/flags/${alpha2Code.toLowerCase()}.svg`,
  };

  const loadingPlaceholderOption = {
    value: '',
    label: `${t('Loading')}...`,
    flagIcon: undefined,
  };

  const [options, setOptions] = React.useState<CountryCodeOption[]>([
    defaultCountryOnLoad,
    loadingPlaceholderOption,
  ]);
  const [selectedOption, setSelectedOption] = React.useState<SingleValue<CountryCodeOption> | null>(
    defaultCountryOnLoad,
  );

  const [queryReference, loadQuery] = useQueryLoader<CountryCodeQuery>(query);

  const onDropdownOpened = () => {
    loadQuery({});
  };

  return (
    <div className={classes.root}>
      <Select<CountryCodeOption>
        name="overrideCountry"
        id="overrideCountry"
        defaultValue={defaultCountryOnLoad}
        onChange={(newValue) => setSelectedOption(newValue)}
        onMenuOpen={onDropdownOpened}
        options={options}
        isSearchable={false}
        components={{ IndicatorSeparator: () => null }}
        formatOptionLabel={(e) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {e.flagIcon && (
              <Image
                src={e.flagIcon}
                alt={e.label}
                width={20}
                height={15}
                style={{
                  marginInlineEnd: 10,
                }}
              />
            )}
            {e.label}
          </div>
        )}
        classNames={{
          container: () => classes.container,
          control: (state) => `${classes.control} ${state.isFocused ? classes.controlFocused : ''}`,
          dropdownIndicator: () => classes.dropdownIndicator,
          indicatorSeparator: () => classes.indicatorSeparator,
          option: (state) =>
            `${classes.option} ${state.isFocused ? classes.optionFocused : ''} ${
              state.isSelected ? classes.optionSelected : ''
            }`,
        }}
      />
      <Suspense fallback={null}>
        {queryReference && (
          <OptionsDisplay queryReference={queryReference} setOptions={setOptions} />
        )}
      </Suspense>
    </div>
  );
}

interface OptionsDisplayProps {
  queryReference: ReturnType<typeof useQueryLoader<CountryCodeQuery>>[0] | null;
  setOptions: React.Dispatch<React.SetStateAction<CountryCodeOption[]>>;
}

function OptionsDisplay({ queryReference, setOptions }: OptionsDisplayProps) {
  const data = usePreloadedQuery(query, queryReference!);

  React.useEffect(() => {
    if (data?.panelConfigurations?.length > 0) {
      const result = data.panelConfigurations
        .map((config) => {
          const prefix = config?.countryDetails?.phoneNumberPrefixes?.[0];
          const alpha2 = config?.countryDetails?.alpha2;
          const alpha3 = config?.countryDetails?.alpha3;

          if (!prefix) return null;

          const option: CountryCodeOption = {
            value: alpha3,
            label: `+${prefix}`,
            ...(alpha2 && {
              flagIcon: `/assets/flags/${alpha2.toLowerCase()}.svg`,
            }),
          };

          return option;
        })
        .filter((option): option is CountryCodeOption => option !== null);

      setOptions(result);
    }
  }, [data, setOptions]);

  return null;
}
